import { WHEEL_RANGES, compileGradeInto, fullGrade, wheelFromPuck, type GradeData, type PrimaryWheelId, type Wheel } from './grade';

/**
 * Shot match ("Match to reference"): grade a shot so its colour matches a
 * reference frame, solved in the colour page's own controls so the result is
 * an ordinary, editable grade.
 *
 * 1. Both frames are sampled (a few thousand pixels) and converted to OkLab
 *    (Ottosson 2020): L lightness, (a, b) the colour axes.
 * 2. Statistics: lightness at seven percentiles (levels and contrast), and
 *    per tonal band (five soft bands from shadows to highlights, placed at
 *    each frame's own percentiles so a darker shot's shadows still meet the
 *    reference's shadows) the mean a and b (colour cast) and mean chroma
 *    (saturation). Both frames are measured the same way, the graded shot
 *    from its graded pixels, so a perfect match scores zero.
 * 3. Skin tones (OkLab hue 20°–75°, chroma 0.02–0.2, L 0.25–0.92, soft
 *    edges) count ×0.2 in the colour statistics, so faces do not drive the cast; after solving,
 *    the colour wheels are scaled back until the shot's skin hue moves at
 *    most 8°.
 * 4. Unknowns: lift, gamma and gain (master and wheel colour each) and a
 *    saturation (a flat Hue vs Sat curve): ten numbers, solved by
 *    Levenberg–Marquardt with numerical derivatives through the real grade
 *    (`compileGradeInto`), with a small penalty that keeps the grade minimal.
 *
 * Pixels are display RGB 0..1 (what the grade works on), interleaved RGB.
 */

export interface Lab {
  L: Float32Array;
  a: Float32Array;
  b: Float32Array;
}

const srgbToLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));

/** OkLab of one display-RGB colour (sRGB transfer). */
export function oklab(r: number, g: number, b: number): [number, number, number] {
  const lr = srgbToLinear(Math.min(1, Math.max(0, r)));
  const lg = srgbToLinear(Math.min(1, Math.max(0, g)));
  const lb = srgbToLinear(Math.min(1, Math.max(0, b)));
  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}

function toLab(rgb: ArrayLike<number>): Lab {
  const n = Math.floor(rgb.length / 3);
  const out = { L: new Float32Array(n), a: new Float32Array(n), b: new Float32Array(n) };
  for (let i = 0; i < n; i++) {
    const [L, a, b] = oklab(rgb[3 * i], rgb[3 * i + 1], rgb[3 * i + 2]);
    out.L[i] = L;
    out.a[i] = a;
    out.b[i] = b;
  }
  return out;
}

/** RGB (0..1) of RGBA bytes (canvas ImageData), skipping transparent pixels, at most `max` pixels (evenly spread). */
export function rgbaToRgb(data: ArrayLike<number>, max = 8192): Float32Array {
  const n = Math.floor(data.length / 4);
  const step = Math.max(1, n / max);
  const out: number[] = [];
  for (let f = 0; f < n; f += step) {
    const i = Math.floor(f) * 4;
    if (data[i + 3] < 128) continue;
    out.push(data[i] / 255, data[i + 1] / 255, data[i + 2] / 255);
  }
  return Float32Array.from(out);
}

/** At most `max` pixels of interleaved RGB, evenly spread (deterministic). */
function subsample(rgb: ArrayLike<number>, max: number): Float32Array {
  const n = Math.floor(rgb.length / 3);
  if (n <= max) return Float32Array.from(rgb as ArrayLike<number>);
  const out = new Float32Array(max * 3);
  for (let k = 0; k < max; k++) {
    const i = Math.floor((k * n) / max) * 3;
    out[3 * k] = rgb[i];
    out[3 * k + 1] = rgb[i + 1];
    out[3 * k + 2] = rgb[i + 2];
  }
  return out;
}

// ---------------------------------------------------------------------------
// Statistics
// ---------------------------------------------------------------------------

export const MATCH_PERCENTILES = [0.02, 0.1, 0.25, 0.5, 0.75, 0.9, 0.98] as const;
const PCT_HALF = 0.025;
const SKIN_WEIGHT = 0.2;
export const SKIN_DRIFT_MAX = 8;

/** 1 inside [lo, hi], 0 beyond ± soft, smooth in between. */
const window1 = (x: number, lo: number, hi: number, soft: number) => smooth(lo - soft, lo + soft, x) * (1 - smooth(hi - soft, hi + soft, x));
function smooth(a: number, b: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}

/** How much a colour is a skin tone (OkLab hue 20°–75°, chroma 0.02–0.2, L 0.25–0.92), with soft edges: 0..1. */
export function skinness(L: number, a: number, b: number): number {
  const c = Math.hypot(a, b);
  const h = (Math.atan2(b, a) * 180) / Math.PI;
  return window1(h, 20, 75, 4) * window1(c, 0.02, 0.2, 0.008) * window1(L, 0.25, 0.92, 0.03);
}

export const isSkin = (L: number, a: number, b: number) => skinness(L, a, b) >= 0.5;

/** Tonal bands: soft ranges of lightness centred on the frame's own 10th, 30th … 90th percentiles. */
export const MATCH_BANDS = 5;

/**
 * Band weights of a pixel with lightness L, given the band centres (the
 * frame's own lightness at evenly spaced percentiles): linear between
 * neighbouring centres, so they sum to 1 and change smoothly with the grade.
 */
function bands(L: number, centres: readonly number[], out: number[]): void {
  out.fill(0);
  const last = centres.length - 1;
  if (L <= centres[0]) out[0] = 1;
  else if (L >= centres[last]) out[last] = 1;
  else {
    let k = 0;
    while (k < last - 1 && L > centres[k + 1]) k++;
    const f = (L - centres[k]) / Math.max(1e-9, centres[k + 1] - centres[k]);
    out[k] = 1 - f;
    out[k + 1] = f;
  }
}

/** Mean of the sorted values in the rank window around percentile p (continuous in the values). */
function rankMean(L: Float32Array, order: readonly number[], p: number, half: number): number {
  const n = order.length;
  const lo = Math.max(0, Math.floor((p - half) * n));
  const hi = Math.min(n, Math.max(lo + 1, Math.ceil((p + half) * n)));
  let s = 0;
  for (let i = lo; i < hi; i++) s += L[order[i]];
  return s / (hi - lo);
}

/**
 * How a frame's statistics are gathered: which pixels make each percentile,
 * and each pixel's band and colour weights (from the frame's own lightness).
 */
interface Layout {
  n: number;
  /** Pixel indices of each percentile's rank window. */
  pct: Int32Array[];
  /** Band weights (n × MATCH_BANDS) with skin down-weighting for the colour statistics. */
  color: Float32Array;
  /** Skin pixels (for the hue drift cap). */
  skin: Int32Array;
}

function layout(lab: Lab): Layout {
  const n = lab.L.length;
  const order = Array.from({ length: n }, (_, i) => i).sort((p, q) => lab.L[p] - lab.L[q] || p - q);
  const centres = Array.from({ length: MATCH_BANDS }, (_, k) => rankMean(lab.L, order, (k + 0.5) / MATCH_BANDS, 0.05));
  const w = new Array(MATCH_BANDS).fill(0);
  const pct = MATCH_PERCENTILES.map((p) => {
    const lo = Math.max(0, Math.floor((p - PCT_HALF) * n));
    const hi = Math.min(n, Math.max(lo + 1, Math.ceil((p + PCT_HALF) * n)));
    return Int32Array.from(order.slice(lo, hi));
  });
  const color = new Float32Array(n * MATCH_BANDS);
  const skin: number[] = [];
  for (let i = 0; i < n; i++) {
    // Skin counts less in the colour statistics (a soft weight, so the statistics stay smooth).
    const sk = skinness(lab.L[i], lab.a[i], lab.b[i]);
    if (sk >= 0.5) skin.push(i);
    bands(lab.L[i], centres, w);
    for (let k = 0; k < MATCH_BANDS; k++) color[MATCH_BANDS * i + k] = w[k] * (1 - (1 - SKIN_WEIGHT) * sk);
  }
  return { n, pct, color, skin: Int32Array.from(skin) };
}

/** Statistics: 7 lightness percentiles, then per band (darkest first) mean a, mean b and mean chroma. */
function stats(lab: Lab, lay: Layout): number[] {
  const out: number[] = [];
  for (const idx of lay.pct) {
    let s = 0;
    for (const i of idx) s += lab.L[i];
    out.push(s / Math.max(1, idx.length));
  }
  for (let k = 0; k < MATCH_BANDS; k++) {
    let w = 0;
    let a = 0;
    let b = 0;
    let c = 0;
    for (let i = 0; i < lay.n; i++) {
      const wi = lay.color[MATCH_BANDS * i + k];
      if (!wi) continue;
      w += wi;
      a += wi * lab.a[i];
      b += wi * lab.b[i];
      c += wi * Math.hypot(lab.a[i], lab.b[i]);
    }
    w = Math.max(1e-9, w);
    out.push(a / w, b / w, c / w);
  }
  return out;
}

/** Weights of the statistics in the fit (colour differences are smaller numbers than lightness). */
const STAT_WEIGHTS = [...MATCH_PERCENTILES.map(() => 1), ...Array.from({ length: MATCH_BANDS }, () => [3, 3, 3]).flat()];

/** A frame's match statistics (what the solver compares). */
export function frameStats(rgb: ArrayLike<number>, max = 4096): number[] {
  const lab = toLab(subsample(rgb, max));
  return stats(lab, layout(lab));
}

/** Weighted RMS difference between two frames' statistics (0 = same levels, cast and saturation). */
export function statsDistance(a: readonly number[], b: readonly number[]): number {
  let s = 0;
  let w = 0;
  for (let i = 0; i < a.length; i++) {
    s += (STAT_WEIGHTS[i] * (a[i] - b[i])) ** 2;
    w += 1;
  }
  return Math.sqrt(s / w);
}

/** Mean hue (degrees) of the skin pixels, or null when there are too few. */
function skinHue(lab: Lab, skin: Int32Array): number | null {
  if (skin.length < 20) return null;
  let a = 0;
  let b = 0;
  for (const i of skin) {
    a += lab.a[i];
    b += lab.b[i];
  }
  return (Math.atan2(b, a) * 180) / Math.PI;
}

// ---------------------------------------------------------------------------
// Parameters ↔ grade
// ---------------------------------------------------------------------------

const WHEELS: readonly PrimaryWheelId[] = ['lift', 'gamma', 'gain'];
/** Unknowns: per wheel [master, puck x, puck y], then saturation. */
export const MATCH_PARAMS = 10;

// Pucks stay inside the square that fits the wheel, so the solver never meets its rim.
const LOWER = [...WHEELS.flatMap((id) => [-WHEEL_RANGES[id].master, -0.7, -0.7]), -1];
const UPPER = [...WHEELS.flatMap((id) => [WHEEL_RANGES[id].master, 0.7, 0.7]), 1];
/** Typical sizes of each unknown (the penalty is relative to these). */
const SCALE = [0.3, 0.6, 0.6, 0.5, 0.6, 0.6, 0.6, 0.6, 0.6, 0.5];

/** Puck position → wheel colour (luma-neutral), as wheelFromPuck but unrounded for the solver. */
function wheelOf(x: number, y: number, id: PrimaryWheelId, master: number): Wheel {
  const d = Math.hypot(x, y);
  if (d > 1) {
    x /= d;
    y /= d;
  }
  const rim = WHEEL_RANGES[id].color / 2;
  const r = 1.403 * y * rim;
  const b = 1.773 * x * rim;
  return { r, g: (-0.299 * r - 0.114 * b) / 0.587, b, y: master };
}

/** The grade for a parameter vector on top of `base` (other controls kept); `round` = as stored. */
export function matchParamsToGrade(p: readonly number[], base: GradeData = {}, round = false): GradeData {
  const g: GradeData = { ...base };
  WHEELS.forEach((id, k) => {
    const [m, x, y] = [p[3 * k], p[3 * k + 1], p[3 * k + 2]];
    g[id] = round ? wheelFromPuck(x, y, id, Math.round(m * 1e4) / 1e4) : wheelOf(x, y, id, m);
  });
  const sat = round ? Math.round(p[9] * 1e4) / 1e4 : p[9];
  g.hueSat = Math.abs(sat) > 1e-6 ? [[0, sat]] : [];
  return g;
}

/** Applies a grade to interleaved RGB pixels. */
export function gradePixels(g: GradeData, rgb: ArrayLike<number>): Float32Array {
  const f = compileGradeInto(fullGrade(g));
  const n = Math.floor(rgb.length / 3);
  const out = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) f(rgb[3 * i], rgb[3 * i + 1], rgb[3 * i + 2], out, 3 * i);
  return out;
}

// ---------------------------------------------------------------------------
// Solver
// ---------------------------------------------------------------------------

export interface MatchResult {
  /** The grade to store (sparse; `base` with lift, gamma, gain and the saturation curve replaced). */
  grade: GradeData;
  /** Statistics distance before and after (statsDistance units). */
  before: number;
  after: number;
  /** How far the shot's skin hue moved (degrees), when it has skin. */
  skinDrift?: number;
  iterations: number;
}

/** Solve a small dense linear system (Gaussian elimination with partial pivoting). */
function solve(A: number[][], b: number[]): number[] | null {
  const n = b.length;
  const M = A.map((row, i) => [...row, b[i]]);
  for (let c = 0; c < n; c++) {
    let p = c;
    for (let r = c + 1; r < n; r++) if (Math.abs(M[r][c]) > Math.abs(M[p][c])) p = r;
    if (Math.abs(M[p][c]) < 1e-14) return null;
    [M[c], M[p]] = [M[p], M[c]];
    for (let r = c + 1; r < n; r++) {
      const f = M[r][c] / M[c][c];
      for (let k = c; k <= n; k++) M[r][k] -= f * M[c][k];
    }
  }
  const x = new Array(n).fill(0);
  for (let r = n - 1; r >= 0; r--) {
    let s = M[r][n];
    for (let k = r + 1; k < n; k++) s -= M[r][k] * x[k];
    x[r] = s / M[r][r];
  }
  return x;
}

/**
 * Grades `target` (its pixels before this grade) to match `reference` (the
 * finished look). `base` = the node's current grade: its other controls are
 * kept and applied while solving. Deterministic.
 */
export function matchGrade(
  target: ArrayLike<number>,
  reference: ArrayLike<number>,
  o: { base?: GradeData; maxPixels?: number; iterations?: number; skinProtect?: boolean; penalty?: number } = {},
): MatchResult {
  const base = { ...(o.base ?? {}) };
  const px = subsample(target, o.maxPixels ?? 3000);
  const refLab = toLab(subsample(reference, o.maxPixels ?? 3000));
  const goal = stats(refLab, layout(refLab));
  const lab0 = toLab(px);
  const lay = layout(lab0);
  const penalty = o.penalty ?? 0.01;
  // Percentiles, bands and skin weights come from the graded pixels themselves, exactly as
  // for the reference (a cast reorders colours by lightness and moves them in and out of skin).
  const statsOf = (lab: Lab) => stats(lab, layout(lab));
  const evalStats = (p: readonly number[]) => statsOf(toLab(gradePixels(matchParamsToGrade(p, base), px)));
  const residual = (p: readonly number[]) => {
    const s = evalStats(p);
    const r = s.map((v, i) => STAT_WEIGHTS[i] * (v - goal[i]));
    for (let k = 0; k < MATCH_PARAMS; k++) r.push((penalty * p[k]) / SCALE[k]);
    return r;
  };
  const cost = (r: number[]) => r.reduce((s, v) => s + v * v, 0);
  const clampP = (p: number[]) => p.map((v, k) => Math.min(UPPER[k], Math.max(LOWER[k], v)));

  const before = statsDistance(stats(lab0, lay), goal);
  const H = 1e-4;
  let total = 0;
  /** Levenberg–Marquardt over the unknowns marked in `free`, from `p0`. */
  const lm = (p0: number[], free: readonly number[], iterations: number): number[] => {
    let p = p0;
    let r = residual(p);
    let c = cost(r);
    let lambda = 1e-2;
    const m = free.length;
    for (let it = 0; it < iterations; it++, total++) {
      // Jacobian by forward differences.
      const J: number[][] = r.map(() => new Array(m).fill(0));
      free.forEach((k, j) => {
        const q = [...p];
        const h = q[k] + H > UPPER[k] ? -H : H;
        q[k] += h;
        const rq = residual(q);
        for (let i = 0; i < r.length; i++) J[i][j] = (rq[i] - r[i]) / h;
      });
      const JtJ = Array.from({ length: m }, (_, a) => Array.from({ length: m }, (_, b) => J.reduce((s, row) => s + row[a] * row[b], 0)));
      const Jtr = Array.from({ length: m }, (_, a) => J.reduce((s, row, i) => s + row[a] * r[i], 0));
      let improved = false;
      let done = false;
      for (let tries = 0; tries < 10 && !improved; tries++) {
        const A = JtJ.map((row, a) => row.map((v, b) => (a === b ? v + lambda * (v + 1e-6) : v)));
        const d = solve(
          A,
          Jtr.map((v) => -v),
        );
        if (!d) {
          lambda *= 4;
          continue;
        }
        const step = [...p];
        free.forEach((k, j) => (step[k] += d[j]));
        const q = clampP(step);
        const rq = residual(q);
        const cq = cost(rq);
        if (cq < c) {
          done = c - cq < 1e-9 * Math.max(1e-3, c);
          p = q;
          r = rq;
          c = cq;
          lambda = Math.max(1e-7, lambda / 3);
          improved = true;
        } else lambda *= 4;
      }
      if (!improved || done) break;
    }
    return p;
  };
  const iterations = o.iterations ?? 40;
  // Levels and saturation first (a well-behaved problem), then the colour wheels with them.
  let p = lm(new Array(MATCH_PARAMS).fill(0), [0, 3, 6, 9], Math.ceil(iterations / 2));
  p = lm(p, Array.from({ length: MATCH_PARAMS }, (_, k) => k), iterations);

  // Skin protection: pull the wheel colours back until the skin hue moves at most SKIN_DRIFT_MAX.
  let skinDrift: number | undefined;
  const hue0 = o.skinProtect === false ? null : skinHue(lab0, lay.skin);
  if (hue0 !== null) {
    const driftOf = (q: readonly number[]) => {
      const h = skinHue(toLab(gradePixels(matchParamsToGrade(q, base), px)), lay.skin)!;
      return Math.abs(((h - hue0 + 540) % 360) - 180);
    };
    const withColour = (k: number) => p.map((v, i) => (i % 3 !== 0 && i < 9 ? v * k : v));
    skinDrift = driftOf(p);
    if (skinDrift > SKIN_DRIFT_MAX) {
      let lo = 0;
      let hi = 1;
      for (let i = 0; i < 20; i++) {
        const mid = (lo + hi) / 2;
        if (driftOf(withColour(mid)) > SKIN_DRIFT_MAX) hi = mid;
        else lo = mid;
      }
      p = withColour(lo);
      skinDrift = driftOf(p);
    }
  }
  const grade = matchParamsToGrade(p, base, true);
  const after = statsDistance(statsOf(toLab(gradePixels(grade, px))), goal);
  return { grade, before, after, ...(skinDrift !== undefined ? { skinDrift: Math.round(skinDrift * 100) / 100 } : {}), iterations: total };
}
